/**
 * ComisionesVentasPanel.jsx — Egresos → Comisiones
 * Lista las comisiones generadas por ventas cobradas (fecha = fecha del pago), permite
 * seleccionar todas o algunas y pagarlas adjuntando la factura del comisionado.
 */
import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { Loader2, Wallet, FileText, Ban, Upload, X, Eye } from "lucide-react";
import { AuthContext } from "../App";
import { toast } from "sonner";
import DateInput from "./ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hoy = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const fmt = (n, m = "PYG") => `${m === "PYG" ? "₲" : m === "USD" ? "US$" : m === "BRL" ? "R$" : m} ${Number(n || 0).toLocaleString("es-PY", { maximumFractionDigits: m === "PYG" ? 0 : 2 })}`;
const INPUT = "w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500";
const LABEL = "block text-xs text-slate-400 mb-1";
const ESTADO_CLS = {
  pendiente: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  pagada: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  anulada: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

export default function ComisionesVentasPanel() {
  const { token, activeEmpresaPropia, hasPermission, user } = useContext(AuthContext);
  const logoTipo = activeEmpresaPropia?.slug;
  const esAdmin = ["admin", "super_admin", "gerente"].includes(user?.role);
  const puedePagar = esAdmin || hasPermission("vendedores.pagar_comisiones");
  const puedeAnular = esAdmin || hasPermission("vendedores.anular_pago_comision");
  const puedeCargarComp = esAdmin || hasPermission("vendedores.cargar_comprobante_comision") || puedePagar;
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }), [token]);

  const [vista, setVista] = useState("comisiones");   // comisiones | pagos
  const [items, setItems] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fUsuario, setFUsuario] = useState("");
  const [fEstado, setFEstado] = useState("pendiente");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [sel, setSel] = useState({});
  const [pagarOpen, setPagarOpen] = useState(false);
  const [compCom, setCompCom] = useState(null);   // comisión a la que se le carga el comprobante

  const qBase = useCallback(() => {
    const q = new URLSearchParams();
    if (logoTipo) q.set("logo_tipo", logoTipo);
    return q;
  }, [logoTipo]);

  const cargar = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const q = qBase();
      if (fUsuario) q.set("usuario_id", fUsuario);
      if (desde) q.set("desde", desde);
      if (hasta) q.set("hasta", hasta);
      if (vista === "comisiones") {
        if (fEstado) q.set("estado", fEstado);
        const r = await fetch(`${API}/admin/comisiones-ventas?${q}`, { headers });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || "Error al cargar comisiones");
        setItems(d); setSel({});
      } else {
        const r = await fetch(`${API}/admin/comisiones-ventas/pagos?${q}`, { headers });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || "Error al cargar pagos");
        setPagos(d);
      }
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [token, qBase, fUsuario, fEstado, desde, hasta, vista, headers]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (!token) return;
    const q = qBase().toString();
    fetch(`${API}/admin/comisiones-ventas/comisionados?${q}`, { headers }).then(r => r.ok ? r.json() : []).then(d => setUsuarios(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/admin/cuentas-bancarias?${q}`, { headers }).then(r => r.ok ? r.json() : []).then(d => setCuentas(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token, qBase, headers]);

  const pendientes = items.filter(c => c.estado === "pendiente");
  const marcadas = pendientes.filter(c => sel[c.id]);
  const todas = pendientes.length > 0 && marcadas.length === pendientes.length;
  const totalMarcado = marcadas.reduce((s, c) => s + Number(c.monto || 0), 0);
  const monedaMarcada = marcadas[0]?.moneda || "PYG";

  const abrirPagar = () => {
    if (!marcadas.length) { toast.error("Seleccioná al menos una comisión"); return; }
    if (new Set(marcadas.map(c => c.usuario_id)).size > 1) { toast.error("Cada pago es para un solo comisionado: filtrá por usuario y volvé a seleccionar"); return; }
    if (new Set(marcadas.map(c => c.moneda || "PYG")).size > 1) { toast.error("Hay comisiones en monedas distintas: pagalas por separado"); return; }
    setPagarOpen(true);
  };

  const verArchivo = async (p) => {
    try {
      const r = await fetch(`${API}/admin/comisiones-ventas/pagos/${p.id}/archivo`, { headers });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error");
      if (!d.archivo) { toast.info("Este pago no tiene archivo adjunto"); return; }
      const w = window.open();
      if (w) w.document.write(`<title>${d.nombre || "Factura"}</title><body style="margin:0"><iframe src="${d.archivo}" style="border:0;width:100vw;height:100vh"></iframe></body>`);
    } catch (e) { toast.error(e.message); }
  };

  const verComprobante = async (c) => {
    try {
      const r = await fetch(`${API}/admin/comisiones-ventas/${c.id}/comprobante/archivo`, { headers });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error");
      if (!d.archivo) { toast.info("Este comprobante no tiene archivo adjunto"); return; }
      const w = window.open();
      if (w) w.document.write(`<title>${d.nombre || "Comprobante"}</title><body style="margin:0"><iframe src="${d.archivo}" style="border:0;width:100vw;height:100vh"></iframe></body>`);
    } catch (e) { toast.error(e.message); }
  };

  const anular = async (p) => {
    if (!window.confirm(`¿Anular el pago ${p.numero}? Las comisiones vuelven a quedar pendientes y el egreso se revierte en la cuenta.`)) return;
    try {
      const r = await fetch(`${API}/admin/comisiones-ventas/pagos/${p.id}/anular`, { method: "POST", headers });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error al anular");
      toast.success("Pago anulado"); cargar();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex bg-white/5 rounded-lg p-1">
          {[["comisiones", "Comisiones"], ["pagos", "Pagos realizados"]].map(([k, l]) => (
            <button key={k} onClick={() => setVista(k)} className={`px-3 py-1.5 text-sm rounded-md ${vista === k ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
        <div className="min-w-[170px]"><label className={LABEL}>Comisionado</label>
          <select className={INPUT} value={fUsuario} onChange={e => setFUsuario(e.target.value)}>
            <option value="">Todos</option>
            {usuarios.map(u => <option key={u.id} value={u.id}>{u.nombre}{u.tipo === "entidad" ? " (vendedor)" : ""}</option>)}
          </select></div>
        {vista === "comisiones" && (
          <div><label className={LABEL}>Estado</label>
            <select className={INPUT} value={fEstado} onChange={e => setFEstado(e.target.value)}>
              <option value="pendiente">Pendientes</option><option value="pagada">Pagadas</option><option value="anulada">Anuladas</option><option value="">Todas</option>
            </select></div>
        )}
        <div><label className={LABEL}>{vista === "pagos" ? "Pago desde" : "Cobro desde"}</label><DateInput value={desde} onChange={e => setDesde(e.target.value)} className={INPUT} /></div>
        <div><label className={LABEL}>Hasta</label><DateInput value={hasta} onChange={e => setHasta(e.target.value)} className={INPUT} /></div>
        {vista === "comisiones" && puedePagar && (
          <button onClick={abrirPagar} disabled={!marcadas.length}
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm">
            <Wallet className="w-4 h-4" /> Pagar seleccionadas{marcadas.length ? ` (${marcadas.length} · ${fmt(totalMarcado, monedaMarcada)})` : ""}
          </button>
        )}
      </div>

      {loading && <div className="py-8 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}

      {!loading && vista === "comisiones" && (
        <div className="bg-arandu-dark-light border border-white/10 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 text-xs text-slate-400">
              <tr>
                <th className="p-3 w-8">{puedePagar && pendientes.length > 0 &&
                  <input type="checkbox" title="Seleccionar todas las pendientes" checked={todas}
                    onChange={e => setSel(e.target.checked ? Object.fromEntries(pendientes.map(c => [c.id, true])) : {})} />}</th>
                <th className="p-3 text-left">Fecha cobro</th><th className="p-3 text-left">Comisionado</th><th className="p-3 text-left">Factura</th>
                <th className="p-3 text-left">Cliente</th><th className="p-3 text-left">Concepto</th>
                <th className="p-3 text-right">Base</th><th className="p-3 text-right">%</th><th className="p-3 text-right">Comisión</th><th className="p-3 text-left">Comprobante</th><th className="p-3 text-left">Estado</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={11} className="p-6 text-center text-slate-500">Sin comisiones</td></tr>}
              {items.map(c => (
                <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02] text-slate-200">
                  <td className="p-3">{puedePagar && c.estado === "pendiente" &&
                    <input type="checkbox" checked={!!sel[c.id]} onChange={e => setSel({ ...sel, [c.id]: e.target.checked })} />}</td>
                  <td className="p-3 whitespace-nowrap">{c.fecha}</td>
                  <td className="p-3">{c.usuario_nombre}</td>
                  <td className="p-3 whitespace-nowrap">{c.factura_numero}{c.factura_anulada && <span className="ml-1 text-[10px] text-red-400">(factura anulada)</span>}</td>
                  <td className="p-3">{c.cliente_nombre}</td>
                  <td className="p-3">{c.tipo === "producto" ? `Producto: ${c.producto_nombre || ""}` : c.tipo === "vendedor" ? "Venta (vendedor)" : "Venta"}</td>
                  <td className="p-3 text-right">{fmt(c.base, c.moneda)}</td>
                  <td className="p-3 text-right">{c.porcentaje}%</td>
                  <td className="p-3 text-right font-medium text-white">{fmt(c.monto, c.moneda)}</td>
                  <td className="p-3 whitespace-nowrap">
                    {c.comp_cargado ? (
                      <span className="inline-flex items-center gap-1 text-emerald-300 text-xs">
                        <FileText className="w-3 h-3" />{c.comp_numero || "Adjunto"}
                        {c.comp_archivo_nombre && <button className="p-0.5 text-slate-400 hover:text-white" title="Ver archivo" onClick={() => verComprobante(c)}><Eye className="w-3.5 h-3.5" /></button>}
                        {puedeCargarComp && c.estado !== "anulada" && !c.comp_en_pago && <button className="p-0.5 text-slate-400 hover:text-white" title="Reemplazar comprobante" onClick={() => setCompCom(c)}><Upload className="w-3.5 h-3.5" /></button>}
                      </span>
                    ) : (puedeCargarComp && c.estado !== "anulada"
                      ? <button className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-xs text-slate-300" onClick={() => setCompCom(c)}><Upload className="w-3 h-3" /> Cargar</button>
                      : <span className="text-slate-500">—</span>)}
                  </td>
                  <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-[10px] border ${ESTADO_CLS[c.estado] || ""}`}>{c.estado}{c.pago_numero ? ` · ${c.pago_numero}` : ""}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && vista === "pagos" && (
        <div className="bg-arandu-dark-light border border-white/10 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 text-xs text-slate-400">
              <tr><th className="p-3 text-left">N°</th><th className="p-3 text-left">Fecha pago</th><th className="p-3 text-left">Comisionado</th><th className="p-3 text-left">Factura del comisionado</th>
                <th className="p-3 text-left">Cuenta</th><th className="p-3 text-right">Comisiones</th><th className="p-3 text-right">Monto</th><th className="p-3 text-left">Estado</th><th className="p-3"></th></tr>
            </thead>
            <tbody>
              {pagos.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-500">Sin pagos</td></tr>}
              {pagos.map(p => (
                <tr key={p.id} className="border-b border-white/5 text-slate-200">
                  <td className="p-3">{p.numero}</td><td className="p-3 whitespace-nowrap">{p.fecha_pago}</td><td className="p-3">{p.usuario_nombre}</td>
                  <td className="p-3">{p.factura_numero || (p.comprobantes_individuales ? <span className="text-slate-300">{p.cantidad} comprobante(s) individuales{(p.comprobantes_numeros || []).length ? `: ${(p.comprobantes_numeros || []).join(", ")}` : ""}</span> : <span className="text-slate-500">—</span>)}{p.factura_razon_social ? <div className="text-xs text-slate-500">{p.factura_razon_social}{p.factura_ruc ? ` · ${p.factura_ruc}` : ""}</div> : null}</td>
                  <td className="p-3">{p.cuenta_nombre}</td><td className="p-3 text-right">{p.cantidad}</td>
                  <td className="p-3 text-right font-medium text-white">{fmt(p.monto, p.moneda)}</td>
                  <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-[10px] border ${p.estado === "pagado" ? ESTADO_CLS.pagada : ESTADO_CLS.anulada}`}>{p.estado}</span></td>
                  <td className="p-3 whitespace-nowrap">
                    {p.factura_archivo_nombre && <button className="p-1 text-slate-400 hover:text-white" title="Ver factura adjunta" onClick={() => verArchivo(p)}><Eye className="w-4 h-4" /></button>}
                    {puedeAnular && p.estado === "pagado" && <button className="p-1 text-slate-400 hover:text-red-400" title="Anular pago" onClick={() => anular(p)}><Ban className="w-4 h-4" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {compCom && (
        <ComprobanteModal comision={compCom} headers={headers}
          onClose={() => setCompCom(null)} onDone={() => { setCompCom(null); cargar(); }} />
      )}

      {pagarOpen && (
        <PagarModal comisiones={marcadas} cuentas={cuentas} logoTipo={logoTipo} headers={headers}
          onClose={() => setPagarOpen(false)} onDone={() => { setPagarOpen(false); cargar(); }} />
      )}
    </div>
  );
}

function PagarModal({ comisiones, cuentas, logoTipo, headers, onClose, onDone }) {
  const moneda = comisiones[0]?.moneda || "PYG";
  const total = comisiones.reduce((s, c) => s + Number(c.monto || 0), 0);
  const cuentasMoneda = cuentas.filter(c => (c.moneda || "PYG") === moneda && c.activa !== false);
  const [cuentaId, setCuentaId] = useState(cuentasMoneda.find(c => c.predeterminada)?.id || cuentasMoneda[0]?.id || "");
  const [fecha, setFecha] = useState(hoy());
  const [notas, setNotas] = useState("");
  const [modoComp, setModoComp] = useState("unico");   // unico | individual | ninguno
  const conFactura = modoComp === "unico";
  const conCompIndiv = comisiones.filter(c => c.comp_cargado).length;
  const [fac, setFac] = useState({ numero: "", timbrado: "", ruc: "", razon_social: "", fecha: hoy(), monto: total, archivo: "", archivo_nombre: "" });
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setFac(p => ({ ...p, [k]: v }));

  const onFile = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > 1_800_000) { toast.error("El archivo supera 1,8 MB"); return; }
    const fr = new FileReader();
    fr.onload = () => setFac(p => ({ ...p, archivo: fr.result, archivo_nombre: f.name }));
    fr.readAsDataURL(f);
  };

  const pagar = async () => {
    if (!cuentaId) { toast.error("Elegí la cuenta de la que sale el pago"); return; }
    if (conFactura && !fac.numero.trim()) { toast.error("Ingresá el N° de la factura del comisionado o elegí otra opción de comprobante"); return; }
    if (modoComp === "individual" && conCompIndiv < comisiones.length) { toast.error(`Faltan comprobantes en ${comisiones.length - conCompIndiv} comisión(es): cargalos en la lista o pagá con una sola factura`); return; }
    setSaving(true);
    try {
      const q = logoTipo ? `?logo_tipo=${encodeURIComponent(logoTipo)}` : "";
      const r = await fetch(`${API}/admin/comisiones-ventas/pagar${q}`, {
        method: "POST", headers,
        body: JSON.stringify({
          comision_ids: comisiones.map(c => c.id), cuenta_id: cuentaId, fecha_pago: fecha, notas,
          usar_comprobantes_individuales: modoComp === "individual",
          factura: conFactura ? { ...fac, monto: Number(fac.monto) || total, archivo: fac.archivo || null, archivo_nombre: fac.archivo_nombre || null } : null,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error al registrar el pago");
      toast.success(`Pago ${d.numero} registrado`);
      onDone();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-semibold">Pagar comisiones — {comisiones[0]?.usuario_nombre}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="bg-white/5 rounded-lg p-3 mb-4 text-sm text-slate-300 flex justify-between">
          <span>{comisiones.length} comisión(es)</span><b className="text-white">{fmt(total, moneda)}</b>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div><label className={LABEL}>Cuenta de pago ({moneda})</label>
            <select className={INPUT} value={cuentaId} onChange={e => setCuentaId(e.target.value)}>
              <option value="">— Elegir —</option>{cuentasMoneda.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select></div>
          <div><label className={LABEL}>Fecha de pago</label><DateInput value={fecha} onChange={e => setFecha(e.target.value)} className={INPUT} /></div>
        </div>

        <div className="mb-3">
          <label className={LABEL}>Comprobante del comisionado</label>
          <select className={INPUT} value={modoComp} onChange={e => setModoComp(e.target.value)}>
            <option value="unico">Una sola factura que cubre todas las comisiones (se registra ahora)</option>
            <option value="individual">Un comprobante por cada comisión (ya cargados: {conCompIndiv} de {comisiones.length})</option>
            <option value="ninguno">Sin comprobante</option>
          </select>
          {modoComp === "individual" && conCompIndiv < comisiones.length && (
            <p className="text-xs text-amber-400 mt-1">Faltan comprobantes en {comisiones.length - conCompIndiv} comisión(es). Cargalos con el botón "Cargar" de la lista.</p>
          )}
        </div>
        {conFactura && (
          <div className="border border-white/10 rounded-xl p-3 space-y-3 mb-4">
            <div className="grid grid-cols-2 gap-3">
              <div><label className={LABEL}>N° de factura *</label><input className={INPUT} value={fac.numero} onChange={e => setF("numero", e.target.value)} placeholder="001-001-0000123" /></div>
              <div><label className={LABEL}>Timbrado</label><input className={INPUT} value={fac.timbrado} onChange={e => setF("timbrado", e.target.value)} /></div>
              <div><label className={LABEL}>RUC / CI</label><input className={INPUT} value={fac.ruc} onChange={e => setF("ruc", e.target.value)} /></div>
              <div><label className={LABEL}>Razón social / Nombre</label><input className={INPUT} value={fac.razon_social} onChange={e => setF("razon_social", e.target.value)} /></div>
              <div><label className={LABEL}>Fecha de la factura</label><DateInput value={fac.fecha} onChange={e => setF("fecha", e.target.value)} className={INPUT} /></div>
              <div><label className={LABEL}>Monto de la factura</label><input type="number" className={INPUT} value={fac.monto} onChange={e => setF("monto", e.target.value)} /></div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm text-slate-200 cursor-pointer">
                <Upload className="w-4 h-4" /> {fac.archivo ? "Cambiar archivo" : "Subir factura (PDF / imagen)"}
                <input type="file" accept="application/pdf,image/*" className="hidden" onChange={onFile} />
              </label>
              {fac.archivo_nombre && <span className="text-xs text-emerald-400 inline-flex items-center gap-1"><FileText className="w-3 h-3" />{fac.archivo_nombre}</span>}
            </div>
          </div>
        )}
        <div className="mb-4"><label className={LABEL}>Notas</label><input className={INPUT} value={notas} onChange={e => setNotas(e.target.value)} /></div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm text-slate-300">Cancelar</button>
          <button onClick={pagar} disabled={saving} className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-sm text-white">
            {saving ? "Registrando..." : `Registrar pago ${fmt(total, moneda)}`}
          </button>
        </div>
      </div>
    </div>
  );
}


function ComprobanteModal({ comision, headers, onClose, onDone }) {
  const [fac, setFac] = useState({
    numero: comision.comp_numero || "", timbrado: comision.comp_timbrado || "", ruc: comision.comp_ruc || "",
    razon_social: comision.comp_razon_social || comision.usuario_nombre || "", fecha: comision.comp_fecha || hoy(),
    monto: comision.comp_monto ?? comision.monto, archivo: "", archivo_nombre: comision.comp_archivo_nombre || "",
  });
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setFac(p => ({ ...p, [k]: v }));
  const onFile = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > 1_800_000) { toast.error("El archivo supera 1,8 MB"); return; }
    const fr = new FileReader();
    fr.onload = () => setFac(p => ({ ...p, archivo: fr.result, archivo_nombre: f.name }));
    fr.readAsDataURL(f);
  };
  const guardar = async () => {
    if (!fac.numero.trim() && !fac.archivo) { toast.error("Ingresá el N° del comprobante o adjuntá el archivo"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/comisiones-ventas/${comision.id}/comprobante`, {
        method: "POST", headers,
        body: JSON.stringify({ ...fac, monto: Number(fac.monto) || null, archivo: fac.archivo || null, archivo_nombre: fac.archivo_nombre || null }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error al guardar");
      toast.success("Comprobante registrado");
      onDone();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-semibold">Comprobante de comisión</h3>
            <p className="text-xs text-slate-400">{comision.usuario_nombre} · Factura {comision.factura_numero} · {fmt(comision.monto, comision.moneda)}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={LABEL}>N° de comprobante / factura *</label><input className={INPUT} value={fac.numero} onChange={e => setF("numero", e.target.value)} placeholder="001-001-0000123" /></div>
          <div><label className={LABEL}>Timbrado</label><input className={INPUT} value={fac.timbrado} onChange={e => setF("timbrado", e.target.value)} /></div>
          <div><label className={LABEL}>RUC / CI</label><input className={INPUT} value={fac.ruc} onChange={e => setF("ruc", e.target.value)} /></div>
          <div><label className={LABEL}>Razón social / Nombre</label><input className={INPUT} value={fac.razon_social} onChange={e => setF("razon_social", e.target.value)} /></div>
          <div><label className={LABEL}>Fecha del comprobante</label><DateInput value={fac.fecha} onChange={e => setF("fecha", e.target.value)} className={INPUT} /></div>
          <div><label className={LABEL}>Monto</label><input type="number" className={INPUT} value={fac.monto} onChange={e => setF("monto", e.target.value)} /></div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm text-slate-200 cursor-pointer">
            <Upload className="w-4 h-4" /> {fac.archivo_nombre ? "Cambiar archivo" : "Subir archivo (PDF / imagen)"}
            <input type="file" accept="application/pdf,image/*" className="hidden" onChange={onFile} />
          </label>
          {fac.archivo_nombre && <span className="text-xs text-emerald-400 inline-flex items-center gap-1"><FileText className="w-3 h-3" />{fac.archivo_nombre}</span>}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm text-slate-300">Cancelar</button>
          <button onClick={guardar} disabled={saving} className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-sm text-white">{saving ? "Guardando..." : "Guardar comprobante"}</button>
        </div>
      </div>
    </div>
  );
}
